/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @version 1.21.0
 * @since 1.0.0
 */
import get from 'lodash/get';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select } from 'redux-saga/effects';

import { tableContext } from '@/main/modules/table/selectors';

import {
    API_HOSTNAME,
    SUBSCRIBER_MOBILE_EXTFILTER,
    SUBSCRIBER_MOBILE_EXTFILTER_CLEAR,
    SUBSCRIBER_MOBILE_EXTFILTER_LIST,
    SUBSCRIBER_MOBILE_EXTFILTER_META,
    SUBSCRIBER_MOBILE_EXTFILTER_SAVE,
    SUBSCRIBER_MOBILE_EXTFILTER_UPDATE,
} from '../../../../../modules/app/Api';
import { requestSaga } from '../../../../../sagas/requestSaga';
import {
    clearFilterFailed,
    clearFilterSuccessful,
    deleteExtFilterFailed,
    deleteExtFilterSuccessful,
    loadExtFilterFailed,
    loadExtFilterSuccessful,
    saveExtFilterFailed,
    saveExtFilterSuccessful,
    sendExtFilterFailed,
    sendExtFilterSuccessful,
    updateExtFilterFailed,
    updateExtFilterSuccessful,
} from '../actions/extFilterApiActions';
import {
    setExtFilterId,
    setExtFilterIdSuccessful,
    setExtFilterItemsSuccessful,
} from '../actions/extFilterConstructorActions';
import { makeSelectExtFilterList } from '../extFilterSelectors';
import { initialState } from '../reducers/extFilterApiReducer';
import { ISendExtFilterPayload } from '../types/extFilterActionTypes';
import { ISubscriberMobileExtFilter } from '../types/extFilterTypes';

export function* loadExtFilterSaga(): SagaIterator {
    try {
        yield put(setExtFilterIdSuccessful(''));
        yield put(setExtFilterItemsSuccessful([{ operation: null }]));
        const timestamp: number = new Date().valueOf();
        const context = yield select(tableContext);
        const conetxtForUrl = context ? `&context=${context}` : '';

        const fitlerItemsUrl = `${API_HOSTNAME}${SUBSCRIBER_MOBILE_EXTFILTER}?_=${timestamp}${conetxtForUrl}`;
        const filterListUrl = `${API_HOSTNAME}${SUBSCRIBER_MOBILE_EXTFILTER_LIST}?_=${timestamp}${conetxtForUrl}`;
        const filterMetaUrl = `${API_HOSTNAME}${SUBSCRIBER_MOBILE_EXTFILTER_META}?_=${timestamp}${conetxtForUrl}`;

        const calls: any = {
            fitlerItems: requestSaga(fitlerItemsUrl, 'GET'),
            filterList: requestSaga(filterListUrl, 'GET'),
            filterMeta: requestSaga(filterMetaUrl, 'GET'),
        };
        const response = yield all(calls);
        const data = {
            extFilter: get(response, 'fitlerItems', initialState.extFilter),
            extFilterList: get(response, 'filterList', initialState.extFilterList).map((item: any) => ({
                ...item,
                id: item.id.toString(),
            })),
            extFilterMeta: get(response, 'filterMeta', initialState.extFilterMeta),
        };
        yield put(loadExtFilterSuccessful(data));
        if (data.extFilter.items && data.extFilter.items.length) {
            yield put(setExtFilterIdSuccessful(data.extFilter.id.toString()));
            yield put(setExtFilterItemsSuccessful(data.extFilter.items));
        }
    } catch (error: any) {
        yield put(loadExtFilterFailed(error));
    }
}

export function* sendExtFilterSaga(action: { payload: ISendExtFilterPayload; type: string }): SagaIterator {
    const context = yield select(tableContext);
    const conetxtForUrl = context ? `?context=${context}` : '';
    const fitlerItemsUrl = `${API_HOSTNAME}${SUBSCRIBER_MOBILE_EXTFILTER}${conetxtForUrl}`;
    try {
        yield call(requestSaga, fitlerItemsUrl, 'POST', action.payload);
        yield put(sendExtFilterSuccessful(action.payload));
        // clear common table filter and recall table list
        action.payload.dropCommonTableFilter();
    } catch (error: any) {
        yield put(sendExtFilterFailed(error));
    }
}

export function* saveExtFilterSaga(action: { payload: ISubscriberMobileExtFilter; type: string }): SagaIterator {
    try {
        const context = yield select(tableContext);
        const conetxtForUrl = context ? `?context=${context}` : '';
        const url = `${API_HOSTNAME}${SUBSCRIBER_MOBILE_EXTFILTER_SAVE}${conetxtForUrl}`;
        const extFilter = (yield call(requestSaga, url, 'POST', action.payload)).toString();
        const newFilter = { ...action.payload, id: extFilter };
        yield put(saveExtFilterSuccessful(newFilter));
        yield put(setExtFilterId(extFilter));
    } catch (error: any) {
        yield put(saveExtFilterFailed(error));
    }
}

export function* updateExtFilterSaga(action: { payload: ISubscriberMobileExtFilter; type: string }): SagaIterator {
    const context = yield select(tableContext);
    const conetxtForUrl = context ? `?context=${context}` : '';
    try {
        const extFilterList = yield select(makeSelectExtFilterList());
        const url = `${API_HOSTNAME}${SUBSCRIBER_MOBILE_EXTFILTER_UPDATE}${conetxtForUrl}`;
        yield call(requestSaga, url, 'PUT', action.payload);
        const newFilterList = yield extFilterList.map((item: ISubscriberMobileExtFilter) => {
            if (item.id === action.payload.id) {
                item = { ...item, ...action.payload };
            }
            return item;
        });
        yield put(updateExtFilterSuccessful(newFilterList));
    } catch (error: any) {
        yield put(updateExtFilterFailed(error));
    }
}

export function* deleteExtFilterSaga(action: { payload: string; type: string }): SagaIterator {
    const context = yield select(tableContext);
    const conetxtForUrl = context ? `?context=${context}` : '';
    try {
        const url = `${API_HOSTNAME}${SUBSCRIBER_MOBILE_EXTFILTER}/${action.payload}${conetxtForUrl}`;
        const extFilterList = yield select(makeSelectExtFilterList());
        yield call(requestSaga, url, 'DELETE');
        const newFilterList = extFilterList.filter(
            (item: ISubscriberMobileExtFilter) => item.id !== action.payload,
        );
        yield put(setExtFilterIdSuccessful(''));
        yield put(setExtFilterItemsSuccessful([{ operation: null }]));
        yield put(deleteExtFilterSuccessful(newFilterList));
    } catch (error: any) {
        yield put(deleteExtFilterFailed(error));
    }
}

export function* clearFilterSaga(): SagaIterator {
    const context = yield select(tableContext);
    const contextForUrl = context ? `?context=${context}` : '';

    const filterClearUrl = `${API_HOSTNAME}${SUBSCRIBER_MOBILE_EXTFILTER_CLEAR}${contextForUrl}`;

    try {
        yield call(requestSaga, filterClearUrl, 'DELETE');

        yield put(clearFilterSuccessful());
        yield put(setExtFilterIdSuccessful(''));
        yield put(setExtFilterItemsSuccessful([{ operation: null }]));
    } catch (error: any) {
        yield put(clearFilterFailed(error));
    }
}
